'use client'

import { createInstance } from '@/i18n/create.instance'
import { i18nConfig } from '@/i18n/i18n.config'
import { init } from '@/i18n/init'
import { i18n as I18nInstance } from 'i18next'
import { useEffect } from 'react'
import { useCookies } from 'react-cookie'
import { getI18n, useTranslation as useTranslationOrg } from 'react-i18next'

const runsOnServerSide = typeof window === 'undefined'

const instance = createInstance()
void init(instance, {
  preload: false,
})

/**
 * i18next only assigns `language` synchronously as long as it is still unset,
 * afterwards `changeLanguage()` defers the assignment until the resources have
 * been loaded - which never resolves within a single (synchronous) SSR pass.
 * Assigning it directly keeps everything the server renders from the language
 * (most notably the locale prefix of every link) in sync with the route, and
 * therefore with what the client renders while hydrating.
 */
const applyLanguageSync = (i18n: I18nInstance, lng: string) => {
  if (!i18n || i18n.language === lng || !i18nConfig.locales.includes(lng)) {
    return
  }

  void i18n.changeLanguage(lng)

  i18n.language = lng
  i18n.languages = i18n.services.languageUtils.toResolveHierarchy(lng)
  i18n.resolvedLanguage = lng
}

export function useTranslation(
  lng?: string,
  ns: string | string[] = i18nConfig.defaultNS,
  options: { keyPrefix?: string } = {}
) {
  const [cookies, setCookie] = useCookies([i18nConfig.cookieName])

  if (runsOnServerSide && lng) {
    // has to happen before `useTranslationOrg()`, as that suspends as long as
    // the namespaces of the language are still loading - the assignment below
    // would never be reached on the first render of a not yet warmed instance
    applyLanguageSync(getI18n() || instance, lng)
  }

  const ret = useTranslationOrg(ns, options)

  const { i18n } = ret

  if (runsOnServerSide && lng) {
    applyLanguageSync(i18n, lng)
  }

  useEffect(() => {
    if (!lng || i18n.language === lng) {
      return
    }

    // `cimode` is not a routable locale - syncing it back to the locale of the
    // route would immediately undo the switch to the translation keys
    if (i18n.language === 'cimode') {
      return
    }

    void i18n.changeLanguage(lng)
  }, [lng, i18n])

  useEffect(() => {
    if (cookies.i18next === lng) {
      return
    }

    setCookie(i18nConfig.cookieName, lng, { path: '/' })
  }, [lng, cookies.i18next, setCookie])

  return ret
}
